import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import firebase from '../firebaseConfig';
import { useEntityList, useEntityDoc, createEntity, updateEntity } from '../data/entities';
import { uploadFile, buildKey, isUploadConfigured } from '../data/uploadService';
import { apiPost, isBackendConfigured } from '../utils/backendApi';
import { API_BASE } from '../data/env';
import { citationHref, citationKind } from '../utils/citationLink';
import HowIKnew from './HowIKnew';
import BrainThink from './BrainThink';
import { loadBrainGraph, THINK_HREF } from '../data/brainGraph';
import { starterPromptsFor } from '../data/consoleModules';
import { studioText } from '../data/consoleCatalog';
import { NUM_BARS } from '../hooks/useVoiceSession';
import { useVoice } from '../hooks/VoiceSessionProvider';
import AgentNavRail, { screenLabel } from './AgentNavRail';
import StageStrip from './StageStrip';
import { scrollPage, safeSection } from './scrollPage';
import { captureScreenOutline } from '../utils/screenOutline';
import { captureScreen } from '../utils/captureScreen';
import { runPageControl, isPageControlTool } from '../utils/pageControls';
import { isPitchTool, handlePitchTool, pitchOutline, usePitchState } from '../utils/pitchRunner';
import { playFilm } from '../utils/filmControl';
import '../utils/pageControls.css';

// The agentOS copilot dock — the console's ONE floating agent (lower right).
//
// Text turns are pure Firestore: sending a message CREATES a queued aiMessage
// doc (rules enforce role/uid/status), the agentOS worker claims and drives
// it, and the triage stage, live plan card, step lights, and approval gate
// below are just onSnapshot rendering the same doc. No sockets, no REST.
//
// Folded in from the old separate VoiceAgent bubble (hooks/useVoiceSession):
// - Voice: start/end a realtime session from the header; transcripts render
//   inline; while live, the SAME input row feeds the voice conversation.
// - Attachments: text, PDF, DOCX and images upload through the normal
//   presigned S3 path (items/copilot/<org>/<session>/) and ride on the
//   aiMessage as `attachments` keys; the worker extracts text / describes
//   images for the planner (shared/attachments.js). Without an upload API
//   configured, text files fall back to inline content.
// - Navigation: the worker's navigate_app tool stamps `navigateTo` on the
//   assistant message and this panel routes; voice navigates client-side.
//   Org-relative paths ("apps", "agents") resolve against the current org.
//   While navigating the panel slides right into AgentNavRail (session and
//   transcript stay live; "NoVA is navigating → <Screen>" pill) — it never
//   just disappears.
// - Voice → copilot: the voice agent's `delegate_to_copilot` client tool
//   enqueues a copilot turn (with any staged attachments) so spoken "build
//   me an app that…" lands in the same planner + approval gate.
// - Guidance (2026-10-04 audit): assistant answers render markdown and
//   carry `citations` (knowledge sources) and `suggestions` — chips the user
//   can act on: navigate (route the console), act (enqueue the instruction
//   as a new turn → plan → approval), ask (prefill a follow-up). The panel
//   opens itself on a member's first visit to an org and starts with the
//   catalog's STARTER_PROMPTS so "how do I build an app" is one click.
const STEP_DOT = {
  pending: '#64748b',
  running: '#f59e0b',
  done: '#34d399',
  error: '#f87171',
};

const TEXT_EXTS = /\.(txt|md|markdown|csv|tsv|json|log|ya?ml|xml|html?)$/i;
const ATTACH_CHAR_BUDGET = 6000; // inline fallback only; aiMessage content maxLen is 8000
const ATTACH_MAX_FILES = 5;
const ATTACH_MAX_BYTES = 8 * 1024 * 1024;
const ATTACH_ACCEPT = '.txt,.md,.markdown,.csv,.tsv,.json,.log,.yaml,.yml,.xml,.html,.pdf,.docx,image/*,text/*';

// 'text' | 'pdf' | 'docx' | 'image' | null — mirrors backend/shared/attachments.js
function attachKind(file) {
  const name = file.name || '';
  const type = file.type || '';
  if (TEXT_EXTS.test(name) || type.startsWith('text/')) return 'text';
  if (/\.pdf$/i.test(name) || type === 'application/pdf') return 'pdf';
  if (/\.docx$/i.test(name) || type.includes('wordprocessingml')) return 'docx';
  if (/\.(png|jpe?g|webp|gif)$/i.test(name) || type.startsWith('image/')) return 'image';
  return null;
}
const KIND_ICON = { text: '📄', pdf: '📕', docx: '📘', image: '🖼' };

// The think stage ([[watch-the-brain-think]]), as on the public dock: a
// header Watch toggle that is always there once the stage is allowed
// (`config/app voice.think.stage`, read from GET /voice/ui), a theatre
// column beside the transcript on wide screens (a pane under the header
// otherwise) that plays the running turn's search live — a copilot turn's
// `think.events` as the worker merges them, or a voice turn's traces as
// each /voice/tool call returns — and rests on the last one. 'auto' opens
// it on the first think event and folds it 4 s after the answer unless
// this person pressed Watch; hide/watch is remembered for the session
// (same key as the dock, so one choice carries across surfaces).
const WATCH_KEY = 'nv.think.watch';
const THINK_UI_DEFAULT = { stage: 'auto' };
const watchPref = () => { try { return sessionStorage.getItem(WATCH_KEY); } catch (_) { return null; } };
const setWatchPref = (v) => { try { sessionStorage.setItem(WATCH_KEY, v); } catch (_) { /* private mode */ } };
function useWide(minPx) {
  const q = `(min-width: ${minPx}px)`;
  const [wide, setWide] = useState(() => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(q).matches : true));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const m = window.matchMedia(q);
    const on = () => setWide(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, [q]);
  return wide;
}
function thinkEventsOf(m) {
  return m?.think?.events?.length ? m.think.events : (m?.trace?.think?.events?.length ? m.trace.think.events : null);
}

// The live think player under a running turn: the user message's
// `think.events` as the worker merges them (same onSnapshot as the plan
// card), over the brain graph when the viewer may read it. After the turn
// finishes the player stays on its last frame; the assistant's "How I knew"
// offers the replay.
function LiveThink({ events, live, orgId, onHide }) {
  const [graph, setGraph] = useState(null);
  useEffect(() => { let on = true; loadBrainGraph().then((g) => { if (on) setGraph(g); }); return () => { on = false; }; }, []);
  return (
    <div className="cp-think" data-nova-ignore="true">
      <BrainThink graph={graph} events={events} live={live} height={260} hrefFor={(d) => citationHref({ docId: d }, { orgId })} LinkComponent={Link} />
      <div className="cp-think-foot">
        <Link to={THINK_HREF} className="cp-watch">Open on the Brain →</Link>
        <button type="button" className="cp-watch" onClick={onHide}>hide</button>
      </div>
    </div>
  );
}

function StepRow({ step, depth = 0 }) {
  return (
    <>
      <div className="cp-step" style={{ paddingLeft: 10 + depth * 16 }}>
        <span
          className={`cp-dot${step.status === 'running' ? ' cp-dot-pulse' : ''}`}
          style={{ background: STEP_DOT[step.status] || '#64748b' }}
        />
        <span className="cp-step-tool">{step.tool}</span>
        {step.tool === 'build_app' && step.args?.name && <span className="cp-step-name">“{step.args.name}”</span>}
        {step.note && <span className="cp-step-note">{step.note}</span>}
        {typeof step.ms === 'number' && <span className="cp-step-ms">{(step.ms / 1000).toFixed(1)}s</span>}
      </div>
      {step.error && <div className="cp-step-err" style={{ paddingLeft: 26 + depth * 16 }}>{step.error}</div>}
      {(step.sub?.steps || []).map((s) => <StepRow key={`${step.id}-${s.id}`} step={s} depth={depth + 1} />)}
    </>
  );
}

function PlanCard({ msg, mine, onApprove }) {
  const plan = msg.plan;
  if (!plan) return null;
  const waiting = msg.status === 'awaiting_approval';
  const auto = msg.approval === 'auto';
  return (
    <div className={`cp-plan${waiting ? ' cp-plan-waiting' : ''}`}>
      <div className="cp-plan-title">
        {plan.title || 'Plan'}
        {plan.replanned ? <span className="cp-plan-replan">revised ×{plan.replanned}</span> : null}
        {auto && <span className="cp-plan-auto" title="NoVA proceeds with ordinary work; only deletions, deploys and paid environments wait for your yes.">{msg.status === 'executing' ? 'doing it now' : 'went ahead'}</span>}
      </div>
      {plan.rationale && <div className="cp-plan-rationale">{plan.rationale}</div>}
      <div>
        {(plan.steps || []).map((s) => <StepRow key={s.id} step={s} />)}
      </div>
      {waiting && (
        <div className="cp-plan-actions">
          <span className="cp-plan-why">Needs your yes{msg.confirmFor ? ` — ${msg.confirmFor.includes('deploy') ? 'this deploys to an environment' : msg.confirmFor.includes('uat') ? 'this adds a paid environment' : 'this cannot be undone'}` : ''}.</span>
          {mine ? (
            <>
              <button className="cs-btn cs-btn-sm" onClick={() => onApprove('approved')} title="Carry out these steps. You will see each one tick off and a summary at the end.">Yes, go ahead</button>
              <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => onApprove('rejected')}>No</button>
            </>
          ) : (
            <span className="cs-help" style={{ fontSize: 12 }}>waiting for the requester's yes</span>
          )}
        </div>
      )}
    </div>
  );
}

const SEEN_KEY = (orgId) => `nv.copilot.seen.${orgId}`;
const SUGGEST_ICON = { navigate: '→', act: '⚡', ask: '?' };

export default function CopilotPanel({ orgId }) {
  const navigate = useNavigate();
  const location = useLocation();
  // First visit to an org: open so a newcomer meets the assistant (and its
  // starter chips) before the sidebar; remembered per org in localStorage.
  const [open, setOpen] = useState(() => {
    try {
      if (window.localStorage.getItem(SEEN_KEY(orgId))) return false;
      window.localStorage.setItem(SEEN_KEY(orgId), String(Date.now()));
      return true;
    } catch { return false; }
  });
  // Navigation mode: when the copilot routes the console, the panel slides
  // right into the edge rail (AgentNavRail) so it doesn't cover the page it
  // just opened — the session stays hot; click re-expands. `nav` names the
  // screen for the banner.
  const [rail, setRail] = useState(false);
  const [watching, setWatching] = useState(() => new Set()); // user message ids with the live think player open
  const [nav, setNav] = useState(null); // { label, at }
  const [sessionId, setSessionId] = useState(null);
  const [text, setText] = useState('');
  const [sendErr, setSendErr] = useState('');
  // Staged files: [{ name, type, size, kind, file }] — uploaded at send time
  // (inline fallback adds `content` instead of `file` when no upload API).
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const me = firebase.auth().currentUser;
  const fileRef = useRef(null);
  const mountedAtRef = useRef(Date.now());
  const navigatedRef = useRef(new Set());
  const attachmentsRef = useRef(attachments);
  useEffect(() => { attachmentsRef.current = attachments; }, [attachments]);

  // Org-relative routes ("apps", "agents", "apps/<id>") resolve against the
  // current org; absolute paths pass through.
  const resolvePath = (p) => {
    const path = String(p || '').trim().replace(/^\.?\//, (m) => (m === '/' ? '/' : ''));
    if (!path) return `/orgs/${orgId}`;
    return path.startsWith('/') ? path : `/orgs/${orgId}/${path}`;
  };

  const openRef = useRef(open);
  useEffect(() => { openRef.current = open; }, [open]);
  // Stage mode: while a LIVE voice session drives the page (navigate,
  // scroll, zoom, click, point) the panel becomes the low bottom band
  // (StageStrip) so the screen stays visible. stageManualRef: null = auto ·
  // true/false = the person's ⤓ / ⤢ pick, kept until the session ends.
  const [stage, setStage] = useState(false);
  const pitch = usePitchState(); // where the pitch is (utils/pitchRunner.js), for the stage rail
  const stageRef = useRef(false);
  useEffect(() => { stageRef.current = stage; }, [stage]);
  const stageManualRef = useRef(null);
  const voiceActiveRef = useRef(false);
  const enterStageAuto = () => {
    if (!voiceActiveRef.current || stageManualRef.current === false) return false;
    setStage(true);
    return true;
  };
  const expandFromStage = () => { stageManualRef.current = false; setStage(false); setRail(false); setOpen(true); };
  const compactToStage = () => { stageManualRef.current = true; setStage(true); };
  useEffect(() => {
    document.body.classList.toggle('nv-stage-on', stage);
    return () => { document.body.classList.remove('nv-stage-on'); };
  }, [stage]);
  const collapseForNav = (to) => {
    setNav({ label: screenLabel(to), at: Date.now() });
    if (enterStageAuto()) return;
    if (openRef.current) setRail(true);
  };
  const restore = () => setRail(false);
  // scroll_page has two shapes: the walkthrough glide (target "next" /
  // "prev" / "top" / "bottom" / a section id on this page, no direction) and
  // the mouse wheel (direction / amount / a scrollable area) in pageControls.
  const isWalkScroll = (args) => {
    if (args?.direction || args?.amount) return false;
    const t = String(args?.target || 'next').trim();
    return /^(next|prev|top|bottom)$/.test(t) || (!!safeSection(t) && !!document.getElementById(t));
  };

  const { docs: sessions } = useEntityList('aiSession', { org: orgId }, { orderBy: 'createdAt', dir: 'desc', enabled: open });
  const scope = useMemo(() => ({ org: orgId, aiSession: sessionId }), [orgId, sessionId]);
  const scopeRef = useRef(scope);
  useEffect(() => { scopeRef.current = scope; }, [scope]);

  // Upload staged files to the raw bucket; returns the aiMessage.attachments
  // shape. Inline-fallback entries (no upload API) have no key and are
  // rendered into the message text instead.
  const uploadStaged = async (staged) => {
    const keyed = [];
    for (const a of staged) {
      if (!a.file) continue;
      const key = buildKey(`items/copilot/${orgId}/${scopeRef.current.aiSession || 'session'}/`, a.name);
      // eslint-disable-next-line no-await-in-loop
      const { key: usedKey } = await uploadFile(a.file, { key, contentType: a.type || 'application/octet-stream' });
      keyed.push({ key: usedKey, name: a.name, type: a.type || '', size: a.size || 0 });
    }
    return keyed;
  };

  // "Screenshot": a JPEG of the viewport (minus the panel itself) staged as
  // an ordinary image attachment, so it rides the same upload → extract →
  // vision path as a pasted picture. The outline (aiMessage.screen) already
  // carries the labels; this is for charts, layouts and anything the text
  // cannot convey. WebGL rooms render blank in html2canvas — known limit.
  const [shooting, setShooting] = useState(false);
  const takeScreenshot = async () => {
    setSendErr('');
    if (attachments.length >= ATTACH_MAX_FILES) { setSendErr(`Up to ${ATTACH_MAX_FILES} attachments per message — send, then attach more.`); return; }
    setShooting(true);
    try {
      const file = await captureScreen();
      setAttachments((prev) => [...prev, { name: file.name, type: 'image/jpeg', size: file.size, kind: 'image', file }]);
    } catch (err) {
      setSendErr(`Screenshot failed: ${err.message}`);
    } finally {
      setShooting(false);
    }
  };

  const inlineBlock = (staged) =>
    staged
      .filter((a) => a.content)
      .map((a) => `--- attached: ${a.name}${a.truncated ? ' (truncated)' : ''} ---\n${a.content}`)
      .join('\n\n');

  // Create the queued copilot turn (the worker does the rest). Shared by the
  // Send button and the voice agent's delegate_to_copilot. The session is
  // resolved lazily once the panel is open — a spoken request that arrives
  // right after the console mounted opens the panel and waits for it.
  const enqueueTurn = async (requestText, staged) => {
    if (!scopeRef.current.aiSession) {
      setOpen(true);
      for (let i = 0; i < 80 && !scopeRef.current.aiSession; i += 1) await new Promise((r) => setTimeout(r, 100)); // eslint-disable-line no-await-in-loop
    }
    const sc = scopeRef.current;
    if (!sc.aiSession) throw new Error('copilot session not ready yet');
    setUploading(true);
    try {
      const keyed = await uploadStaged(staged);
      const content = [String(requestText || '').trim(), inlineBlock(staged)].filter(Boolean).join('\n\n').slice(0, 8000);
      if (!content) throw new Error('nothing to send');
      const page = `${location.pathname}${location.search || ''}`.slice(0, 300);
      // What is on screen right now (redacted DOM outline) rides with the
      // route so NoVA can explain THIS screen, not just name it.
      const screen = captureScreenOutline({ maxChars: 3500 });
      await createEntity('aiMessage', sc, { role: 'user', content, uid: me.uid, status: 'queued', attachments: keyed, page, ...(screen ? { screen } : {}) });
      stampSessionApp(page);
    } finally {
      setUploading(false);
    }
  };

  // The studio reads an app's conversations by aiSession.appId: the first
  // turn (typed or spoken) on a ?app= screen stamps the session once.
  const stampedRef = useRef(false);
  const stampSessionApp = (page) => {
    const appId = /[?&]app=([A-Za-z0-9_-]+)/.exec(page || '')?.[1];
    const sc = scopeRef.current;
    if (!appId || !sc.aiSession || stampedRef.current) return;
    const cur = (sessions || []).find((s) => s.id === sc.aiSession);
    if (cur?.appId) { stampedRef.current = true; return; }
    stampedRef.current = true;
    updateEntity('aiSession', { org: orgId }, sc.aiSession, { appId }).catch(() => { stampedRef.current = false; });
  };

  const liveRunRef = useRef(''); // the Lab run the screen last reported (stop_run target)
  const delegatedRef = useRef({ t: 0, request: '' }); // last spoken hand-over (dedupe)
  // The session lives in VoiceSessionProvider (it survives entering and
  // leaving this console); the panel supplies the console's client tools.
  const voice = useVoice(async (name, args) => {
      if (name === 'navigate_app') {
        const to = resolvePath(args?.path);
        collapseForNav(to);
        navigate(to);
        return { ok: true, navigatedTo: to, note: 'Opened. If the user asked for more than opening this screen (start research, run a test, draft the concept…), continue NOW in this same turn — delegate_to_copilot with the rest of the request. Do not stop after navigating.' };
      }
      if (name === 'scroll_page' && isWalkScroll(args)) {
        enterStageAuto();
        scrollPage(String(args?.target || 'next'));
        return { ok: true, scrolled: String(args?.target || 'next') };
      }
      if (isPitchTool(name)) {
        // The pitch from inside a console: the nodes live on the public
        // pages, so the first step leaves the console (the session survives
        // in VoiceSessionProvider; the dock out there takes over the tools).
        enterStageAuto();
        return handlePitchTool(name, args, { navigate: (to) => { collapseForNav(to); navigate(to); }, scrollPage });
      }
      if (name === 'play_film') {
        enterStageAuto();
        return playFilm(args);
      }
      if (isPageControlTool(name)) {
        // Mouse-and-keyboard primitives run now and answer with what happened
        // (utils/pageControls.js refuses marked / irreversible controls).
        enterStageAuto();
        return runPageControl(name, args);
      }
      if (name === 'approve_plan') {
        // Spoken consent: same field the Approve button writes (rules only
        // let the requester set `approval`), on the newest plan waiting
        // for THIS user — never an older one, never someone else's.
        const decision = args?.decision === 'rejected' ? 'rejected' : 'approved';
        const waiting = [...(messagesRef.current || [])].reverse()
          .find((m) => m.role === 'user' && m.uid === me?.uid && m.status === 'awaiting_approval');
        if (!waiting) return { ok: false, error: 'no plan is waiting for approval right now' };
        try {
          await updateEntity('aiMessage', scopeRef.current, waiting.id, { approval: decision });
          return { ok: true, decision, plan: waiting.plan?.title || '', note: decision === 'approved' ? 'Approved — the copilot is carrying out the steps now; you will be told when it finishes.' : 'Rejected — nothing was changed.' };
        } catch (err) {
          return { ok: false, error: String(err.message || err).slice(0, 200) };
        }
      }
      if (name === 'delegate_to_copilot') {
        // Spoken build/create/run requests become a copilot turn: same
        // planner, same approval gate, staged attachments included.
        // One hand-over per ask: while a turn of mine is still queued /
        // planning / executing / waiting, or within a few seconds of the
        // last hand-over, the model gets told it is already underway
        // instead of a second turn (the realtime model re-issued the call
        // on every follow-up and once queued sixty for one request).
        const request = String(args?.request || '').trim();
        const busy = (messagesRef.current || []).find((m) => m.role === 'user' && m.uid === me?.uid && m.source !== 'voice'
          && ['queued', 'planning', 'executing', 'awaiting_approval'].includes(m.status) && Date.now() - (m.createdAt || 0) < 15 * 60e3);
        const recent = Date.now() - delegatedRef.current.t < 12000;
        if (busy || recent) {
          return {
            ok: true,
            alreadyUnderway: true,
            note: busy?.status === 'awaiting_approval'
              ? 'The copilot already has a plan waiting for the user\'s yes — do NOT hand over again. Ask them to approve or reject it.'
              : 'The copilot already has this request and is working on it — do NOT hand it over again or rephrase it. Say one short sentence that it is underway, then wait for the COPILOT note.',
          };
        }
        delegatedRef.current = { t: Date.now(), request };
        const staged = attachmentsRef.current;
        try {
          await enqueueTurn(String(args?.request || ''), staged);
          setAttachments([]);
          setRail(false);
          setOpen(true);
          return {
            ok: true,
            note: `Request handed to the copilot with ${staged.length} attachment(s). It is planning and will carry the work out right away; you will get a COPILOT STARTED note, then COPILOT REPLIED. Only deletions, deploys and paid environments pause for a yes. Tell the user in one sentence that you are on it.`,
          };
        } catch (err) {
          return { ok: false, error: String(err.message || err).slice(0, 200) };
        }
      }
      if (name === 'stop_run') {
        // Spoken "stop": cancel the Lab run named in the last BROWSING /
        // LAB RUN note (or the one on screen) through the normal cancel
        // route — the only write voice makes besides approve_plan.
        // The screen's run id wins: the model has quoted fan-out JOB ids
        // from list_jobs here ("lab-…-xxxx"), which 404 on the run route.
        const runId = String(liveRunRef.current || args?.runId || '').trim();
        if (!runId) return { ok: false, error: 'no Lab run is in progress right now' };
        try {
          await apiPost(`/lab/${orgId}/runs/${runId}/cancel`, {});
          return { ok: true, runId, note: 'Stopping — tell the user it is winding down and what was found so far will be kept.' };
        } catch (err) {
          return { ok: false, error: String(err.message || err).slice(0, 200) };
        }
      }
      return undefined; // backend handles the rest
  });

  // A live voice session carried in from outside the console (the user
  // asked NoVA to open this organization): show the panel as the rail so
  // the voice state is visible and the plan card has somewhere to appear.
  useEffect(() => {
    if (voice.active) { setOpen(true); setRail(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  voiceActiveRef.current = voice.active;
  // Session over → stage mode ends and the manual pick resets to automatic.
  useEffect(() => {
    if (voice.active) return;
    stageManualRef.current = null;
    if (stageRef.current) { setStage(false); setRail(false); setOpen(true); }
  }, [voice.active]);

  // Screens talk to the live voice session through one window event — the
  // Lab's concept board announces CONCEPT BOARD READY, the live browser
  // pane sends a BROWSING note per visit ([[studio-journey]]). Each detail:
  // { text, respond?, runId? }. Dropped silently when no session is live.
  const voiceRef = useRef(voice);
  useEffect(() => { voiceRef.current = voice; }, [voice]);
  useEffect(() => {
    const onCtx = (e) => {
      const d = e?.detail || {};
      if (d.runId !== undefined) liveRunRef.current = d.runId || '';
      if (!d.text || !voiceRef.current.active) return;
      voiceRef.current.sendSystem(String(d.text).slice(0, 4000), { respond: d.respond !== false, role: d.role });
    };
    window.addEventListener('nv:voice-context', onCtx);
    return () => window.removeEventListener('nv:voice-context', onCtx);
  }, []);

  // The studio step follows the voice session onto the app's screens. The
  // /voice/session prompt only carries the step for the page the session
  // STARTED on; after "start the studio" NoVA lands on builder?app=… with no
  // idea she is now the interviewer, and handed every answer to the copilot
  // (which failed them as "sub-goal needs clarification"). Same text the
  // backend uses (studioText), re-sent whenever the app or its step changes.
  const studioAppId = /[?&]app=([A-Za-z0-9_-]+)/.exec(location.search || '')?.[1] || '';
  const { doc: studioApp } = useEntityDoc('app', { org: orgId }, studioAppId);
  const studioStep = studioApp?.journey?.step || '';
  useEffect(() => {
    if (!voice.active || !studioAppId || !studioStep) return;
    const text = studioText({ id: studioAppId, ...studioApp });
    if (!text) return;
    voice.sendSystem(`${text}\nInterview answers, reactions and feedback are conversation: reply by voice yourself. Hand over to the copilot (delegate_to_copilot) only for the ladder's actions — research, drafting or revising the concept, stories, build, test — and only once per action.`, { respond: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice.active, studioAppId, studioStep]);

  // Voice transcripts persist into the copilot session (status 'none', source
  // 'voice' — never a turn) so the interview NoVA ran by voice is part of
  // what the planner and the concept draft read. Posted as they finalize,
  // keyed by the line's id so a user line the provider re-sent longer
  // (useVoiceSession replaces it in place) is upserted, not duplicated;
  // each line carries its own time so a batch keeps its order. A failed
  // post is retried with the next batch.
  const postedRef = useRef(new Map()); // id → content posted
  useEffect(() => {
    if (!sessionId || !isBackendConfigured()) return;
    const list = voice.messages || [];
    if (!list.length) { postedRef.current = new Map(); return; }
    const posted = postedRef.current;
    const pending = list.filter((m) => m.id && m.content && posted.get(m.id) !== m.content);
    if (!pending.length) return;
    const items = pending.map((m) => ({ id: m.id, role: m.role, content: m.content, at: m.at || null }));
    for (const m of pending) posted.set(m.id, m.content);
    const page = `${location.pathname}${location.search || ''}`.slice(0, 300);
    apiPost(`/copilot/${orgId}/sessions/${sessionId}/transcript`, { items, page }).then(() => stampSessionApp(page)).catch(() => { for (const m of pending) posted.delete(m.id); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice.messages, sessionId]);
  const { docs: messages } = useEntityList('aiMessage', scope, { orderBy: 'createdAt', dir: 'asc', enabled: open && !!sessionId });
  const messagesRef = useRef(messages);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // The rooms listen: parts the copilot names light up for a few seconds
  // (TopologyScene Narration). Each new assistant reply is announced once.
  const narratedRef = useRef(new Set());
  useEffect(() => {
    for (const m of messages || []) {
      if (m.role !== 'assistant' || !m.content || m.createdAt < mountedAtRef.current || narratedRef.current.has(m.id)) continue;
      narratedRef.current.add(m.id);
      window.dispatchEvent(new CustomEvent('nv-copilot-said', { detail: { text: String(m.content).slice(0, 2000) } }));
    }
  }, [messages]);

  // Voice hears what the copilot does: a plan waiting for approval is read
  // back (NoVA asks; approve_plan needs an explicit yes), finished turns
  // and errors are narrated. Each (message, status) is announced once; only
  // turns newer than this mount.
  const voiceToldRef = useRef(new Set());
  useEffect(() => {
    if (!voice.active) return;
    for (const m of messages || []) {
      if (m.createdAt < mountedAtRef.current || m.source === 'voice') continue;
      if (m.role === 'user' && m.uid === me?.uid) {
        const key = `${m.id}:${m.status}`;
        if (voiceToldRef.current.has(key)) continue;
        if (m.status === 'awaiting_approval' && m.plan) {
          voiceToldRef.current.add(key);
          const steps = (m.plan.steps || []).map((s, i) => `${i + 1}. ${s.tool}${s.note ? ` — ${s.note}` : ''}`).join('; ');
          // Notes are terse FACTS. The realtime model read whole notes aloud
          // — label, instructions and all — so what to do with them lives in
          // the voice prompt (CONSOLE NOTES in voiceAgent.js), not here.
          voice.sendSystem(`COPILOT PLAN WAITING FOR APPROVAL — "${m.plan.title || 'Plan'}". ${m.plan.rationale || ''} Steps: ${steps}. Needs a yes because: ${m.confirmFor || 'a step that cannot be undone'}.`, { respond: true });
        } else if (m.status === 'executing' && m.plan && m.approval === 'auto') {
          voiceToldRef.current.add(key);
          const steps = (m.plan.steps || []).map((s) => s.note || s.tool).join('; ');
          // She just said "I'm on it" for the spoken hand-over; a second
          // "I'm on it" for the STARTED note read as stuttering.
          voice.sendSystem(`COPILOT STARTED — "${m.plan.title || 'Plan'}": ${steps}.`, { respond: Date.now() - delegatedRef.current.t > 20000 });
        } else if (m.status === 'error') {
          voiceToldRef.current.add(key);
          voice.sendSystem(`COPILOT TURN FAILED: ${String(m.error || 'unknown error').slice(0, 300)}.`, { respond: true });
        }
      } else if (m.role === 'assistant' && m.content) {
        const key = `${m.id}:assistant`;
        if (voiceToldRef.current.has(key)) continue;
        voiceToldRef.current.add(key);
        voice.sendSystem(`COPILOT REPLIED (on screen already): ${String(m.content).slice(0, 1500)}`, { respond: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, voice.active]);

  // Latest session OF THIS USER, or lazily create one on first open. Sessions
  // are org-scoped docs; without the createdBy filter every member landed in
  // whichever session was newest in the org (seen 2026-10-12: a partner's
  // turns in the founder's thread).
  useEffect(() => {
    if (!open || sessionId || sessions === null || !me?.uid) return;
    const mine = sessions.filter((s) => s.createdBy === me.uid);
    if (mine.length) {
      setSessionId(mine[0].id);
      return;
    }
    createEntity('aiSession', { org: orgId }, { title: 'New session', createdBy: me?.uid })
      .then(setSessionId)
      .catch((e) => setSendErr(e.message));
  }, [open, sessionId, sessions, orgId, me]);

  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, voice.messages, voice.streamingText]);

  // Handoff from the public dock: /orgs/<id>?nova=<request> — the dock
  // could not plan (no org), so it opened this console with the request.
  // Open, wait for the session, enqueue once, and drop the param so a
  // reload does not re-run it.
  const novaParamRef = useRef(null);
  useEffect(() => {
    const q = new URLSearchParams(location.search).get('nova');
    if (q && !novaParamRef.current) { novaParamRef.current = String(q).slice(0, 1500); setRail(false); setOpen(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  useEffect(() => {
    const pending = novaParamRef.current;
    if (!pending || !sessionId) return;
    novaParamRef.current = '';
    const params = new URLSearchParams(location.search);
    params.delete('nova');
    navigate({ pathname: location.pathname, search: params.toString() ? `?${params}` : '' }, { replace: true });
    enqueueTurn(pending, []).catch((e) => setSendErr(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // "Ask NoVA" in the console header (and any module) opens this panel —
  // NoVA is the product name for the copilot surface (deck-ia-console).
  useEffect(() => {
    const onAsk = (e) => {
      setRail(false);
      setOpen(true);
      const q = e?.detail?.prompt;
      if (q) setText(String(q));
      // Home's "Build it with NoVA" asks for the documents up front; the
      // dispatch runs inside the click gesture so the picker is allowed.
      if (e?.detail?.attach) fileRef.current?.click();
    };
    window.addEventListener('nv:ask-nova', onAsk);
    return () => window.removeEventListener('nv:ask-nova', onAsk);
  }, []);

  // The worker's navigate_app / page controls: fresh assistant messages
  // carrying navigateTo route the console, ones carrying pageControls run
  // them in order on the screen (once per message; history never replays).
  useEffect(() => {
    for (const m of messages || []) {
      if (m.role !== 'assistant' || (!m.navigateTo && !m.pageControls?.length)) continue;
      if (m.createdAt < mountedAtRef.current || navigatedRef.current.has(m.id)) continue;
      navigatedRef.current.add(m.id);
      let wait = 0;
      if (m.navigateTo) {
        const to = resolvePath(m.navigateTo);
        collapseForNav(to);
        navigate(to);
        wait = 600;
      }
      for (const pc of (Array.isArray(m.pageControls) ? m.pageControls : []).slice(0, 6)) {
        setTimeout(() => { try { runPageControl(pc.name, pc.args || {}); } catch (_) {} }, wait);
        wait += 900;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const busy = (messages || []).some((m) => ['queued', 'planning', 'executing'].includes(m.status));

  /* ── The think stage (see the note above LiveThink) ── */
  const [watchThink, setWatchThink] = useState(() => watchPref() === 'on');
  const [thinkUi, setThinkUi] = useState(null);
  useEffect(() => {
    if (!open || thinkUi || !isBackendConfigured()) return undefined;
    let on = true;
    fetch(`${API_BASE}/voice/ui`).then((r) => (r.ok ? r.json() : null)).then((d) => { if (on) setThinkUi({ ...THINK_UI_DEFAULT, ...(d?.think || {}) }); }).catch(() => { if (on) setThinkUi(THINK_UI_DEFAULT); });
    return () => { on = false; };
  }, [open, thinkUi]);
  const thinkMode = (thinkUi || THINK_UI_DEFAULT).stage; // 'auto' | 'pill' | 'off'
  const thinkAvail = thinkMode !== 'off';
  const runningTurn = useMemo(() => [...(messages || [])].reverse().find((m) => m.role === 'user' && !['done', 'error'].includes(m.status) && thinkEventsOf(m)), [messages]);
  const lastTurnThink = useMemo(() => {
    const all = [...(messages || []), ...(voice.messages || [])];
    for (let i = all.length - 1; i >= 0; i -= 1) { const ev = thinkEventsOf(all[i]); if (ev) return ev; }
    return [];
  }, [messages, voice.messages]);
  const thinkBusy = !!runningTurn || voice.liveThink.length > 0;
  const liveEvents = runningTurn ? runningTurn.think.events : voice.liveThink;
  const [restThink, setRestThink] = useState([]);
  useEffect(() => { if (liveEvents.length) setRestThink(liveEvents); }, [liveEvents]);
  const thinkEvents = liveEvents.length ? liveEvents : (restThink.length ? restThink : lastTurnThink);
  const thinkOn = thinkAvail && thinkEvents.length > 0;
  useEffect(() => {
    if (thinkMode !== 'auto' || !thinkBusy || !liveEvents.length || watchPref() === 'off') return;
    setWatchThink(true);
  }, [thinkMode, thinkBusy, liveEvents.length]);
  useEffect(() => {
    if (thinkMode !== 'auto' || thinkBusy || !watchThink || watchPref() === 'on') return undefined;
    const t = setTimeout(() => setWatchThink(false), 4000);
    return () => clearTimeout(t);
  }, [thinkMode, thinkBusy, watchThink]);
  const toggleWatch = () => { setWatchThink((v) => { setWatchPref(v ? 'off' : 'on'); return !v; }); };
  const wideTheatre = useWide(1000);
  const showTheatre = thinkAvail && watchThink && wideTheatre && !rail;
  const showPane = thinkAvail && watchThink && !wideTheatre && !rail;
  const [stageGraph, setStageGraph] = useState(null);
  useEffect(() => { if (!watchThink || stageGraph) return undefined; let on = true; loadBrainGraph().then((g) => { if (on) setStageGraph(g); }); return () => { on = false; }; }, [watchThink, stageGraph]);
  const stagePlayer = (height) => (
    <BrainThink graph={stageGraph} events={thinkEvents} live={thinkBusy} height={height} hrefFor={(d) => citationHref({ docId: d }, { orgId })} LinkComponent={Link} empty="Ask something — the search plays here as NoVA answers." />
  );

  const addFiles = async (list) => {
    setSendErr('');
    const canUpload = isUploadConfigured();
    let count = attachments.length;
    for (const file of Array.from(list || [])) {
      const kind = attachKind(file);
      if (!kind) {
        setSendErr(`${file.name}: attach text, PDF, DOCX or images — other binaries go to Listen datasets.`);
        continue;
      }
      if (file.size > ATTACH_MAX_BYTES) {
        setSendErr(`${file.name}: files over 8 MB are too large for a copilot turn.`);
        continue;
      }
      if (count >= ATTACH_MAX_FILES) {
        setSendErr(`Up to ${ATTACH_MAX_FILES} attachments per message — send, then attach more.`);
        break;
      }
      if (canUpload) {
        count += 1;
        setAttachments((prev) => [...prev, { name: file.name, type: file.type || '', size: file.size, kind, file }]);
        continue;
      }
      // No upload API: text files inline (legacy path); binaries can't travel.
      if (kind !== 'text') {
        setSendErr(`${file.name}: uploads are not configured here — only text files can be inlined.`);
        continue;
      }
      const used = attachments.reduce((n, a) => n + (a.content?.length || 0), 0);
      const room = ATTACH_CHAR_BUDGET - used;
      if (room <= 0) {
        setSendErr('Attachment budget for one message is full — send, then attach more.');
        break;
      }
      // eslint-disable-next-line no-await-in-loop
      const raw = await file.text();
      count += 1;
      setAttachments((prev) => [...prev, { name: file.name, type: file.type || '', size: file.size, kind, content: raw.slice(0, room), truncated: raw.length > room }]);
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const send = async (e) => {
    e.preventDefault();
    const typed = text.trim();
    if (!typed && !attachments.length) return;
    setSendErr('');
    // A live voice session owns the input row: typed text joins the spoken
    // conversation. Staged attachments stay put — they travel with the
    // voice agent's next delegate_to_copilot (build/create requests).
    if (voice.active) {
      const content = [typed, inlineBlock(attachments)].filter(Boolean).join('\n\n');
      if (content && voice.sendText(content)) {
        setText('');
        if (attachments.some((a) => a.file)) setSendErr('Attachments stay staged for the copilot — ask the agent to build with them.');
        else setAttachments([]);
      }
      return;
    }
    if (!sessionId || busy || uploading) return;
    const staged = attachments;
    setText('');
    setAttachments([]);
    try {
      await enqueueTurn(typed, staged);
    } catch (err) {
      setSendErr(err.message);
      setAttachments(staged); // give the files back so the user can retry
    }
  };

  const newSession = async () => {
    const id = await createEntity('aiSession', { org: orgId }, { title: 'New session', createdBy: me?.uid });
    setSessionId(id);
  };

  const approve = (msgId) => (decision) =>
    updateEntity('aiMessage', scope, msgId, { approval: decision }).catch((e) => setSendErr(e.message));

  // Suggestion chips under an answer (and the starter chips): navigate →
  // route the console; act → a new turn with that instruction (planner +
  // approval gate as always); ask → prefill the input so they can edit.
  const onSuggestion = async (s) => {
    if (s.kind === 'navigate') {
      const to = resolvePath(s.value);
      collapseForNav(to);
      navigate(to);
      return;
    }
    if (s.kind === 'act') {
      if (!sessionId || busy || uploading) { setText(s.value); return; }
      const staged = attachments;
      setAttachments([]);
      try { await enqueueTurn(s.value, staged); } catch (err) { setSendErr(err.message); setAttachments(staged); }
      return;
    }
    setText(s.value);
    if (s.attach) fileRef.current?.click();
  };
  const onStarter = (p) => onSuggestion({ kind: p.attach ? 'ask' : 'act', value: p.text, attach: p.attach });

  // One vocabulary for the voice controls across stages: the STATUS says
  // what NoVA is doing and what you can do ("Listening — go ahead"), the
  // ACTION button always names the thing it does ("End voice", "Cancel"
  // while connecting) — never the state, which read as a label and left
  // people unsure how to stop.
  const stateLabel = voice.activity
    || (voice.state === 'listening' ? 'Listening' :
    voice.state === 'speaking' ? 'Speaking' :
    voice.state === 'processing' || voice.state === 'thinking' ? 'Working on that' :
    voice.state === 'connecting' ? 'Connecting' : 'Voice');
  const stateHint = voice.activity ? ''
    : voice.state === 'listening' ? 'go ahead'
      : voice.state === 'speaking' ? 'talk to interrupt'
        : voice.state === 'connecting' ? 'a moment' : '';
  const endLabel = voice.state === 'connecting' ? 'Cancel' : '■ End voice';

  if (stage) {
    return (
      <StageStrip
        name="NoVA"
        live={voice.active}
        voiceState={voice.state}
        stateLabel={stateLabel}
        clock={`${Math.floor(voice.elapsed / 60)}:${String(voice.elapsed % 60).padStart(2, '0')}`}
        muted={voice.muted}
        onToggleMute={voice.toggleMute}
        onEnd={voice.stop}
        onExpand={expandFromStage}
        barsRef={voice.barsRef}
        messages={voice.messages}
        streamingText={voice.streamingText}
        pitch={pitchOutline(pitch)}
        onPitchJump={(title) => voice.sendText(`Go to ${title}.`)}
        onPitchStop={() => voice.sendText('Stop the pitch.')}
        think={thinkAvail && thinkEvents.length ? { events: thinkEvents, live: thinkBusy } : null}
        onThinkExpand={() => { expandFromStage(); setWatchThink(true); }}
      />
    );
  }

  return (
    <>
      {!(open && rail) && (
        <button
          className={`cp-fab${open ? ' cp-fab-open' : ''}${voice.active ? ' cp-fab-live' : ''}`}
          onClick={() => { setRail(false); setOpen(!open); }}
          aria-label={open ? 'Close NoVA' : 'Ask NoVA'}
          title={open ? 'Close' : 'Ask NoVA — help, directions, or build it for you'}
        >
          <span className="cp-fab-glyph" aria-hidden="true">{open ? '✕' : '◈'}</span>
          {!open && <span className="cp-fab-label">Ask NoVA</span>}
        </button>
      )}
      {open && rail && (
        <AgentNavRail
          name="NoVA"
          nav={nav}
          live={voice.active}
          stateLabel={stateLabel}
          clock={`${Math.floor(voice.elapsed / 60)}:${String(voice.elapsed % 60).padStart(2, '0')}`}
          muted={voice.muted}
          onToggleMute={voice.toggleMute}
          onEnd={voice.stop}
          onRestore={restore}
        />
      )}
      {open && (
        <div className={`cp-panel${rail ? ' cp-panel-rail' : ''}${showTheatre ? ' cp-panel-theatre' : ''}`} aria-hidden={rail}>
         <div className="cp-body">
          <div className="cp-head">
            <span>NoVA <span className="cs-help" style={{ margin: 0, fontWeight: 400 }}>· agentOS copilot</span></span>
            <div className="cp-head-actions">
              {thinkAvail && (
                <button
                  type="button"
                  className={`cs-btn cs-btn-outline cs-btn-sm cp-watch-head${watchThink ? ' is-on' : ''}`}
                  onClick={toggleWatch}
                  aria-pressed={watchThink}
                  title={watchThink ? 'Hide the brain-search view' : 'Show how NoVA searches the brain as it answers'}
                  data-nova-ignore="true"
                >
                  {thinkBusy && thinkOn ? '● ' : ''}{watchThink ? 'Watching' : 'Watch'}
                </button>
              )}
              {voice.configured && (
                <button
                  className={`cs-btn cs-btn-sm${voice.active ? ' cp-voice-end' : ' cs-btn-outline'}`}
                  onClick={voice.active ? voice.stop : voice.start}
                  title={voice.active ? 'End the voice session — the transcript stays' : 'Start a voice session'}
                >
                  {voice.active ? endLabel : '🎙 Voice'}
                </button>
              )}
              {voice.active && (
                <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={compactToStage} title="Stage mode — shrink to a strip at the bottom so the page stays visible" aria-label="Shrink NoVA to the stage strip">⤓</button>
              )}
              <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={newSession}>New session</button>
            </div>
          </div>

          {voice.active && (
            <div className={`cp-voice is-${voice.state}`} role="status" aria-live="polite">
              <span className="cp-voice-state">
                <i className="cp-voice-dot" aria-hidden="true" />
                <b>{stateLabel}</b>{stateHint ? <span className="cp-voice-hint"> — {stateHint}</span> : null}
              </span>
              <div ref={voice.barsRef} className="cp-bars">
                {Array.from({ length: NUM_BARS }).map((_, i) => <span key={i} className="cp-bar" />)}
              </div>
              <span className="cp-voice-clock">{Math.floor(voice.elapsed / 60)}:{String(voice.elapsed % 60).padStart(2, '0')}</span>
              <button className={`cs-btn cs-btn-outline cs-btn-sm${voice.muted ? ' cp-mic-off' : ''}`} onClick={voice.toggleMute} aria-pressed={voice.muted} title={voice.muted ? 'Unmute the microphone' : 'Mute the microphone — NoVA keeps talking'}>
                {voice.muted ? '🔇 Unmute' : '🎙 Mute'}
              </button>
            </div>
          )}
          {voice.error && <div className="cp-step-err" style={{ padding: '0 12px' }}>{voice.error}</div>}
          {showPane && (
            <div className="cp-stage-pane" data-nova-ignore="true">
              {stagePlayer(200)}
            </div>
          )}

          <div className="cp-msgs">
            {(messages || []).length === 0 && voice.messages.length === 0 && (
              <div className="cp-empty">
                <p className="cp-empty-lead">Hi — I’m NoVA. Ask me how anything here works, tell me where you want to go, or
                  ask me to build it for you. Type, talk (🎙), or attach documents and images (📎). I go ahead with ordinary work and
                  only ask before deleting, deploying, changing organization policy, or adding a paid environment.</p>
                <div className="cp-chips" role="list" aria-label="Suggested starts">
                  {starterPromptsFor(`${location.pathname}${location.search || ''}`).map((p) => (
                    <button key={p.label} type="button" role="listitem" className="cp-chip" onClick={() => onStarter(p)} disabled={busy || uploading}>
                      {p.attach ? '📎 ' : ''}{p.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {(messages || []).filter((m) => !(m.source === 'voice' && m.createdAt >= mountedAtRef.current)).map((m) => (
              <div key={m.id} className={`cp-msg cp-msg-${m.role}`}>
                {m.source === 'voice' ? (
                  <div className="cp-bubble cp-bubble-voice" title="voice transcript">{m.content}</div>
                ) : m.role === 'assistant' ? (
                  <div className="cp-bubble cp-md"><ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown></div>
                ) : (
                  <div className="cp-bubble">{m.content}</div>
                )}
                {m.role === 'assistant' && m.navigateTo && (
                  <div className="cp-navline"><span aria-hidden="true">→</span> Opened {screenLabel(resolvePath(m.navigateTo))}</div>
                )}
                {m.role === 'assistant' && m.externalUrl?.url && /^https:\/\//.test(m.externalUrl.url) && (
                  <div className="cp-navline">
                    <span aria-hidden="true">→</span>{' '}
                    <a href={m.externalUrl.url} target="_blank" rel="noopener noreferrer">{m.externalUrl.label || 'Open link'}</a>
                  </div>
                )}
                {m.role === 'assistant' && Array.isArray(m.citations) && m.citations.length > 0 && (
                  <div className="cp-cites">
                    {m.citations.map((c) => {
                      const href = citationHref(c, { orgId });
                      return href
                        ? <Link key={c.ref} to={href} className={`cp-cite is-link is-${citationKind(c)}`} title={`${c.docId} — open`}>[{c.ref}] {c.title}</Link>
                        : <span key={c.ref} className="cp-cite" title={c.docId}>[{c.ref}] {c.title}</span>;
                    })}
                  </div>
                )}
                {m.role === 'assistant' && m.trace && <HowIKnew trace={m.trace} LinkComponent={Link} hrefFor={(h) => citationHref(h, { orgId })} loadGraph={loadBrainGraph} thinkHref={THINK_HREF} />}
                {m.role === 'assistant' && Array.isArray(m.suggestions) && m.suggestions.length > 0 && (
                  <div className="cp-chips">
                    {m.suggestions.map((s, i) => (
                      <button key={`${m.id}-${i}`} type="button" className={`cp-chip cp-chip-${s.kind}`} onClick={() => onSuggestion(s)} disabled={s.kind === 'act' && (busy || uploading)}>
                        <span aria-hidden="true">{SUGGEST_ICON[s.kind] || ''}</span> {s.label}
                      </button>
                    ))}
                  </div>
                )}
                {m.role === 'user' && m.stage && ['planning', 'executing', 'awaiting_approval'].includes(m.status) && (
                  <div className="cp-stage">
                    <span className="cp-dot cp-dot-pulse" style={{ background: '#22d3ee' }} />
                    {m.stage}
                    {m.think?.events?.length > 0 && (
                      <button type="button" className={`cp-watch ${watching.has(m.id) ? 'is-on' : ''}`} onClick={() => setWatching((prev) => { const n = new Set(prev); if (n.has(m.id)) n.delete(m.id); else n.add(m.id); return n; })} aria-pressed={watching.has(m.id)} title="Watch the brain think">
                        {watching.has(m.id) ? 'hide' : 'watch'}
                      </button>
                    )}
                  </div>
                )}
                {m.role === 'user' && watching.has(m.id) && m.think?.events?.length > 0 && (
                  <LiveThink events={m.think.events} live={!['done', 'error'].includes(m.status)} orgId={orgId} onHide={() => setWatching((prev) => { const n = new Set(prev); n.delete(m.id); return n; })} />
                )}
                {m.role === 'user' && <PlanCard msg={m} mine={m.uid === me?.uid} onApprove={approve(m.id)} />}
                {m.status === 'error' && m.error && <div className="cp-step-err">{m.error}</div>}
              </div>
            ))}
            {voice.messages.length > 0 && <div className="cp-voice-divider">voice transcript</div>}
            {voice.messages.map((m) => (
              <div key={m.id} className={`cp-msg cp-msg-${m.role}`}>
                <div className="cp-bubble cp-bubble-voice">{m.content}</div>
                {m.trace && <HowIKnew trace={m.trace} LinkComponent={Link} hrefFor={(h) => citationHref(h, { orgId })} loadGraph={loadBrainGraph} thinkHref={THINK_HREF} />}
              </div>
            ))}
            {voice.streamingText && (
              <div className="cp-msg cp-msg-assistant">
                <div className="cp-bubble cp-bubble-voice">{voice.streamingText}▍</div>
              </div>
            )}
            {voice.activity && !voice.streamingText && (
              <div className="cp-msg cp-msg-assistant" role="status" aria-live="polite">
                <div className="cp-bubble cp-bubble-voice">{voice.activity}{voice.activitySecs ? ` · ${voice.activitySecs}s` : ''}…</div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {sendErr && <div className="cp-step-err" style={{ padding: '0 12px' }}>{sendErr}</div>}
          {attachments.length > 0 && (
            <div className="cp-attach-row">
              {attachments.map((a, i) => (
                <span key={`${a.name}-${i}`} className="cp-attach-chip">
                  {KIND_ICON[a.kind] || '📄'} {a.name}{a.truncated ? ' ✂' : ''}
                  <button type="button" aria-label={`Remove ${a.name}`} onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}>×</button>
                </span>
              ))}
            </div>
          )}
          <form className="cp-form" onSubmit={send}>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept={ATTACH_ACCEPT}
              style={{ display: 'none' }}
              onChange={(e) => addFiles(e.target.files)}
            />
            <button type="button" className="cp-attach-btn" title="Attach documents — PDF, Word, text, images" aria-label="Attach documents" onClick={() => fileRef.current?.click()}>📎<span className="cp-attach-label">Attach</span></button>
            {isUploadConfigured() && (
              <button type="button" className="cp-attach-btn" title="Show NoVA your screen — attaches a picture of this page (3D scenes may come out blank)" aria-label="Attach a screenshot of this screen" disabled={shooting} onClick={takeScreenshot}>📷<span className="cp-attach-label">{shooting ? 'Capturing…' : 'Screenshot'}</span></button>
            )}
            <input
              className="cp-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={
                voice.active ? 'Type to the voice agent…'
                  : uploading ? 'uploading attachments…'
                    : busy ? 'copilot is working…'
                      : 'Tell the copilot what to do…'
              }
            />
            <button
              className="cs-btn cs-btn-sm"
              type="submit"
              disabled={voice.active ? !(text.trim() || attachments.length) : (busy || uploading || !(text.trim() || attachments.length))}
            >
              {uploading ? 'Sending…' : 'Send'}
            </button>
          </form>
         </div>
         {showTheatre && (
           <aside className="cp-theatre" aria-label="How NoVA is thinking" data-nova-ignore="true">
             <header className="cp-theatre-head">
               <span>{thinkBusy ? 'Thinking' : thinkEvents.length ? 'How I thought' : 'Watch NoVA think'}</span>
               <button type="button" className="cp-watch" onClick={toggleWatch}>hide</button>
             </header>
             {stagePlayer(320)}
             <Link to={THINK_HREF} className="cp-watch cp-theatre-more">Open on the Brain →</Link>
           </aside>
         )}
        </div>
      )}
    </>
  );
}
